import { useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Gs1FieldOption } from '../intake.api';

export interface TargetFieldSelectProps {
  options: Gs1FieldOption[];
  value: string | null;
  onChange: (field: string | null) => void;
  disabled?: boolean;
}

/**
 * h5-5: a searchable target-field dropdown over the GS1 field list. Built as a filter input +
 * native `<select>` rather than a custom ARIA combobox: the native element already gives full
 * keyboard support (arrow keys, type-ahead, Enter/Escape) for free (NFR9), and the filter input
 * narrows the (~250-entry) option list without needing a hand-built listbox/popup pattern.
 */
export function TargetFieldSelect({ options, value, onChange, disabled }: TargetFieldSelectProps) {
  const { t } = useTranslation();
  const [filter, setFilter] = useState('');
  const filterId = useId();
  const selectId = useId();

  const filteredOptions = useMemo(() => {
    const query = filter.trim().toLowerCase();
    if (query === '') return options;
    return options.filter((option) => option.label.toLowerCase().includes(query) || option.name.toLowerCase().includes(query));
  }, [options, filter]);

  return (
    <div className="flex flex-col gap-1">
      <label className="sr-only" htmlFor={filterId}>{t('intake.review.targetField.filterLabel')}</label>
      <input
        id={filterId}
        type="text"
        value={filter}
        onChange={(event) => setFilter(event.target.value)}
        placeholder={t('intake.review.targetField.filterPlaceholder')}
        disabled={disabled}
        className="h-8 rounded-[var(--radius-control)] border border-[var(--border)] bg-[var(--background)] px-2 text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--secondary)]"
      />
      <label className="sr-only" htmlFor={selectId}>{t('intake.review.targetField.selectLabel')}</label>
      <select
        id={selectId}
        value={value ?? ''}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value === '' ? null : event.target.value)}
        className="h-9 rounded-[var(--radius-control)] border border-[var(--border)] bg-[var(--background)] px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--secondary)]"
      >
        <option value="">{t('intake.review.targetField.noneOption')}</option>
        {filteredOptions.map((option) => (
          <option key={option.name} value={option.name}>{option.label}</option>
        ))}
      </select>
    </div>
  );
}
